import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { Department } from '@app/features/scoping/models/department.model'
import { LanguageService } from '@app/core/service/language.service'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { Role } from '@app/features/scoping/models/role.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ScopeVersion } from '@app/core/model/scope-version'
import { TableColumnKey } from '../../ui-components/scope-ui-table/table-column-key.enum'
import { User } from '@app/core/model/user.model'

export const getUserColumns = (currentScope: ScopeVersion, user: User, langService: LanguageService) => ({
  [TableColumnKey.CREATED_BY]: {
    key: TableColumnKey.CREATED_BY,
    name: 'Created By',
    selected: false,
    field: 'createdBy.fullName',
  },
  [TableColumnKey.OWNER]: { key: TableColumnKey.OWNER, name: 'Owner', selected: false, field: 'owner.fullName' },
  [TableColumnKey.DATE_CREATED]: {
    key: TableColumnKey.DATE_CREATED,
    name: 'Date created',
    selected: false,
    field: 'createdTs',
  },
  [TableColumnKey.QUANTITY]: {
    key: TableColumnKey.QUANTITY,
    name: 'Quantity',
    selected: true,
    field: 'quantity',
  },
  [TableColumnKey.START_DATE]: {
    key: TableColumnKey.START_DATE,
    name: 'Start Date',
    selected: true,
    isVisibleInList: () => user.company.hasApplicationSetting(ApplicationSetting.SCOPE__DELIVERABLE__STARTEND_DATE),
    field: 'startDate',
  },
  [TableColumnKey.END_DATE]: {
    key: TableColumnKey.END_DATE,
    name: 'End Date',
    selected: false,
    isVisibleInList: () => user.company.hasApplicationSetting(ApplicationSetting.SCOPE__DELIVERABLE__STARTEND_DATE),
    field: 'endDate',
  },
  [TableColumnKey.MARK_UP]: {
    key: TableColumnKey.MARK_UP,
    name: 'Markup',
    selected: false,
    isVisibleInList: () => currentScope.hasPrivilege(Privilege.SCOPE_MARKUP__VIEW, user),
    value: (entity: ScopeSection | Deliverable) => (entity instanceof Deliverable ? entity.getMarkup() : entity.markup),
  },
  [TableColumnKey.PROFIT]: {
    key: TableColumnKey.PROFIT,
    name: 'Profit',
    isVisibleInList: () => currentScope.hasPrivilege(Privilege.SCOPE_MARKUP__VIEW, user),
    value: (entity: ScopeSection | Deliverable) =>
      entity instanceof Deliverable ? entity.getTotalProfit() : entity.totalProfit,
  },
  [TableColumnKey.SCOPEMARK_HOURS]: {
    key: TableColumnKey.SCOPEMARK_HOURS,
    name: `${langService.get('scope_mark')} Hours`,
    selected: false,
    isVisibleInList: () => user.company.hasApplicationSetting(ApplicationSetting.PREDICT__SCOPEMARK__COMPONENTS),
    field: 'scopeMarkTotalHours',
  },
  [TableColumnKey.SCOPEMARK_PRICE]: {
    key: TableColumnKey.SCOPEMARK_PRICE,
    name: `${langService.get('scope_mark')} Price`,
    selected: false,
    isVisibleInList: () => user.company.hasApplicationSetting(ApplicationSetting.PREDICT__SCOPEMARK__COMPONENTS),
    field: 'scopeMarkTotalSellingPrice',
  },
  [TableColumnKey.AGENCY_HOURS]: {
    key: TableColumnKey.AGENCY_HOURS,
    name: `${langService.get('agency')} Hours`,
    selected: true,
    value: (entity: ScopeSection | Deliverable | ScopeComponent | Department | Role) => {
      if (entity instanceof Deliverable) return entity.totalRateCardHours
      if (entity instanceof ScopeSection) return entity.rateCardTotalHours
      if (entity instanceof ScopeComponent || entity instanceof Department) return entity.getTotalRateCardHours()

      return entity.getRateCardHours()
    },
  },
  [TableColumnKey.UNIT_PRICE]: {
    key: TableColumnKey.UNIT_PRICE,
    name: 'Rate',
    selected: true,
    value: (entity: ScopeSection | Deliverable | ScopeComponent | Department | Role) => {
      if (entity instanceof Deliverable) return entity.getUnitSellingPrice()
      if (entity instanceof ScopeComponent) return entity.getUnitSellingPrice()

      return null
    },
  },
  [TableColumnKey.BUDGET]: { key: TableColumnKey.BUDGET, name: 'Budget', selected: false, field: 'budget' },
  [TableColumnKey.BALANCE]: { key: TableColumnKey.BALANCE, name: 'Balance', selected: false, field: 'budgetBalance' },
  [TableColumnKey.DELIVERY_STATUS]: {
    key: TableColumnKey.DELIVERY_STATUS,
    name: 'Delivery Status',
    selected: false,
    isVisible: function () {
      return this.selected && currentScope.isStateApproved()
    },
    isVisibleInList: () => currentScope.isStateApproved(),
  },
  [TableColumnKey.AGENCY_PRICE]: {
    key: TableColumnKey.AGENCY_PRICE,
    name: `${langService.get('agency')} Price`,
    selected: true,
    value: (entity: ScopeSection | Deliverable | ScopeComponent | Department | Role) => {
      if (entity instanceof Deliverable || entity instanceof ScopeComponent || entity instanceof Department)
        return entity.getTotalSellingPrice()
      if (entity instanceof Role) return entity.rateCardTotalSpentPrice

      return entity.totalSellingPrice
    },
  },
  [TableColumnKey.SCOPE__TRAFFICKING__DELIVERABLE_ACTUALS]: {
    key: TableColumnKey.SCOPE__TRAFFICKING__DELIVERABLE_ACTUALS,
    name: 'Show Actuals',
    selected: false,
    isVisibleInList: () =>
      user.company.hasApplicationSetting(ApplicationSetting.SCOPE__TRAFFICKING__DELIVERABLE_ACTUALS),
    field: 'traffickedPercentageCompleteValue',
  },
})
